import React, { FC } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { CreateTransfer } from '../Add';
import { type Action } from '~/lib/authz/eval';

import { AccountsSequence } from './AccountsSequence';

/**
 * ACH-specific wiring for the reusable {@link AccountsSequence}. It registers
 * `ach.bankAccountId` and `ach.counterpartyId` with react-hook-form and maps the
 * ACH transfer direction onto the sequence: a CREDIT pushes funds from your bank
 * account → counterparty (bank account originates); a DEBIT pulls from the
 * counterparty → your bank account (counterparty originates). Descriptions,
 * permissions and the create-modal type are all ACH flavour supplied here.
 */
export const AchAccountsSequence: FC = () => {
  const { control, watch } = useFormContext<CreateTransfer>();
  const type = watch('ach.type') as 'DEBIT' | 'CREDIT' | undefined;
  const amount = watch('ach.amount') as number | undefined;

  const bank = useController({ control, name: 'ach.bankAccountId', rules: { required: true }, defaultValue: '' });
  const counterparty = useController({
    control,
    name: 'ach.counterpartyId',
    rules: { required: true },
    defaultValue: '',
  });

  const isDebit = type === 'DEBIT';
  const permission: Action['permission'] | undefined =
    type === 'DEBIT' ? 'achDebits' : type === 'CREDIT' ? 'achCredits' : undefined;
  const unauthorizedText = `You do not have permission to use ${isDebit ? 'ACH debits' : 'ACH credits'} on this bank account.`;

  // Descriptions track the selected direction so they describe what actually
  // happens: a DEBIT pulls funds from the counterparty into your account; a
  // CREDIT pushes funds from your account out to the counterparty.
  const bankDescription = isDebit
    ? 'Select the bank account the funds will be deposited into.'
    : 'Select the bank account the funds will be sent from.';
  const counterpartyDescription = isDebit
    ? 'Select or create the counterparty the funds will be pulled from.'
    : 'Select or create the counterparty that will receive the funds.';

  return (
    <AccountsSequence
      originatesFrom={isDebit ? 'counterparty' : 'bankAccount'}
      bankAccount={{
        label: 'Bank Account',
        description: bankDescription,
        value: (bank.field.value as string) || '',
        onChange: bank.field.onChange,
        onBlur: bank.field.onBlur,
        hasError: Boolean(bank.fieldState.error) && bank.fieldState.isTouched,
        permission,
        unauthorizedText,
        amount,
        dataCy: 'form-field-ach.bankAccountId',
      }}
      counterparty={{
        label: 'Counterparty',
        description: counterpartyDescription,
        value: (counterparty.field.value as string) || '',
        onChange: counterparty.field.onChange,
        onBlur: counterparty.field.onBlur,
        hasError: Boolean(counterparty.fieldState.error) && counterparty.fieldState.isTouched,
        counterpartyType: 'ach',
        dataCy: 'form-field-ach.counterpartyId',
      }}
    />
  );
};

export default AchAccountsSequence;
